import React, { useState } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { cameraService } from '../../../services/central-api/cameras';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import Toast from 'react-native-toast-message';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import getBestContrastColor from '../../../services/best-contrast-color';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Filter from './Filters';
import { useAuth } from '../../../context/Auth';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import { useQuery } from '@tanstack/react-query';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import WatchVideoModal from './WatchVideoModal';
import { RootNavigation } from '../../../Routes';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';
import Badge from '../../../components/Badge';

export default function Downloads({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();

    const route = useRoute<RouteProp<CamerasParamList, 'Downloads'>>();

    const [filters, setFilters] = useFilters<CamerasParamList['Downloads']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isDownloading, setIsDownloading] = useState(false);

    const [showWatchModal, setShowWatchModal] = useState<boolean>(false);
    const [selectedDownload, setSelectedDownload] = useState<Download | null>(null);
    const [videosUrls, setVideosUrls] = useState<{ [key: number]: { url: string; generatedAt: moment.Moment; }; }>({});

    const isFocused = useIsFocused();
    const { data, isInitialLoading } = useQuery({
        queryKey: ['camerasDownloads', filters],
        queryFn: () => cameraService.getDownloads(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    function handleFilters() {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter !== '' ? textFilter : undefined,
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    async function getVideoUrl(id: number) {
        const validUrl = videosUrls[id];

        if (validUrl) {
            if (moment().diff(validUrl.generatedAt, 'minutes') < 5) {
                return validUrl;
            }
        }

        const generatedAt = moment();
        try {
            setIsDownloading(true);
            const video = await cameraService.getDownload(id);

            const videoUrlObj = {
                url: video.resultUrl,
                generatedAt: generatedAt
            };
            setVideosUrls({
                ...videosUrls,
                [id]: videoUrlObj
            });
            return videoUrlObj;
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsDownloading(false);
        }
    }

    function getStatus(status: 'success' | 'failed' | 'pending') {
        if (status === 'success') {
            return <Badge mode='SQUARE' color='green'>{translate('available')}</Badge>;
        } else if (status === 'pending') {
            return <Badge mode='SQUARE' color='orange'>{translate('processing')}</Badge>;
        }
        return <Badge mode='SQUARE' color='red'>{translate('fail')}</Badge>;
    }

    return (
        <Container>
            <Header title={translate('camerasDownloads')} />
            <Card>
                <DataTableHeader
                    title={translate('registeredCamerasDownloads')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('status'),
                                cell: row => <View>
                                    {getStatus(row.status)}
                                </View>,
                                wrap: true,
                                grow: 0
                            },
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true,
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBodyUnit && row.actingBodyUnit.actingBody ? row.actingBodyUnit.actingBody.name : '',
                                wrap: true,
                            },
                            {
                                name: translate('actingBodyUnit'),
                                selector: row => row.actingBodyUnit && row.actingBodyUnit.actingBody ? row.actingBodyUnit.name : '',
                                wrap: true,
                            },
                            {
                                name: translate('requester'),
                                selector: row => row.user?.name || '',
                                wrap: true,
                            },
                            {
                                name: translate('camera'),
                                selector: row => row.camera?.title || '',
                                wrap: true,
                            },
                            {
                                name: translate('downloadPeriod'),
                                selector: row => `${moment(+row.msStart).format('DD/MM/YYYY')} - ${moment(+row.msStart).format('HH:mm')} ${translate('to')} ${moment(+row.msFinish).format('HH:mm')}`,
                                wrap: true,
                            },
                            {
                                name: translate('requestedDate'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                                wrap: true,
                            },
                            {
                                name: translate('tags'),
                                cell: row => {
                                    if (!row.tags) {
                                        return '';
                                    }

                                    const tags = row.tags.map((tag) =>
                                        <Badge
                                            mode='ROUNDED'
                                            key={tag.id}
                                            backgroundColor={tag.color}
                                            textColor={getBestContrastColor(tag.color)}>
                                            {tag.name}
                                        </Badge>
                                    );

                                    return <View style={{
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        width: '100%',
                                        gap: 5,
                                        paddingVertical: 4,
                                    }}>
                                        {tags}
                                    </View>;
                                },
                            },
                            {
                                button: true,
                                cell: row => {
                                    if (row.status !== 'success') {
                                        return null;
                                    }
                                    const actions: Action[] = [];
                                    actions.push({
                                        content: translate('watch'),
                                        onPress: async () => {
                                            const videoUrl = await getVideoUrl(row.id);
                                            if (!videoUrl) {
                                                return;
                                            }
                                            setShowWatchModal(true);
                                            setSelectedDownload(row);
                                        }
                                    });
                                    actions.push({
                                        content: translate('download'),
                                        onPress: async () => {
                                            const videoUrl = await getVideoUrl(row.id);
                                            if (!videoUrl) {
                                                return;
                                            }
                                            openExternalLinkInNewTab(videoUrl.url);
                                        }
                                    });
                                    return <DropdownButton actions={actions} isLoading={isDownloading} size={100} />;
                                }
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isInitialLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
            {selectedDownload && <WatchVideoModal
                isModalVisible={showWatchModal}
                setModalVisible={setShowWatchModal}
                videoUrl={videosUrls[selectedDownload.id].url}
            />
            }
        </Container>
    );
}
